import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { addSupplier, Supplier, updateSupplier } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { SupplierModalAction } from "../MyLists/SuppliersTable";

interface UpsertSupplierModalProps extends ModalProps {
  companyId?: string;
  supplier?: Supplier;
  type: SupplierModalAction;
}

export function UpsertSupplierModal({
  companyId,
  supplier,
  type,
  ...props
}: UpsertSupplierModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addSupplierMutation = useMutation({ mutationFn: addSupplier });
  const updateSupplierMutation = useMutation({ mutationFn: updateSupplier });

  const supplierSchema = z.object({
    name: z.string().min(1, t("common.Name is required")),
    phone: z.string().min(1, t("common.Phone number is required")),
    adresse: z.string().min(1, t("common.Address is required")),
    email: z.string().email(t("common.Invalid email address")),
  });

  type SupplierSchema = z.infer<typeof supplierSchema>;

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<SupplierSchema>({
    resolver: zodResolver(supplierSchema),
  });

  const handleSaveClick = handleSubmit((data) => {
    if (!companyId) return;

    if (type === SupplierModalAction.ADD) {
      addSupplierMutation.mutate(
        {
          ...data,
          companyId: companyId,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["suppliersByCompany"] });
            reset();
            props.onClose?.();
            toast.success(t("food.Supplier has been added successfully"));
          },
          onError: (error) => {
            toast.error(t("food.Failed to add supplier") + error.message);
          },
        },
      );
      return;
    }

    updateSupplierMutation.mutate(
      {
        ...data,
        companyId: companyId,
        id: supplier?.id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["suppliersByCompany"] });
          reset();
          props.onClose?.();
          toast.success(t("food.Supplier has been updated successfully"));
        },
        onError: (error) => {
          toast.error(t("food.Failed to update supplier") + error.message);
        },
      },
    );
  });

  const handleOnClose = () => {
    reset();
    props.onClose?.();
  };

  useEffect(() => {
    if (supplier) {
      reset(supplier);
    }
  }, [reset, supplier]);

  return (
    <Modal
      {...props}
      onClose={handleOnClose}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={handleOnClose}
            disabled={
              addSupplierMutation.isPending || updateSupplierMutation.isPending
            }
          >
            {t("common.Discard")}
          </Button>
          <Button
            onClick={handleSaveClick}
            loading={
              addSupplierMutation.isPending || updateSupplierMutation.isPending
            }
          >
            {t("food.Save Supplier")}
          </Button>
        </>
      }
      title={`${type === SupplierModalAction.ADD ? t("food.Add Supplier") : t("food.Edit Supplier")} `}
    >
      <form>
        <div className="flex flex-col">
          <Label title={t("food.Name of the Supplier")} required>
            <Input
              {...register("name")}
              name="name"
              type="text"
              defaultValue={supplier?.name}
            />
          </Label>
          <ErrorMessage message={errors.name?.message} />
        </div>
        <div className="flex flex-col">
          <Label title={t("common.Email")} required>
            <Input
              {...register("email")}
              name="email"
              type="text"
              defaultValue={supplier?.email}
            />
          </Label>
          <ErrorMessage message={errors.email?.message} />
        </div>
        <div className="flex flex-col justify-end">
          <Label title={t("common.Phone Number")} required>
            <Input
              {...register("phone")}
              name="phone"
              type="number"
              defaultValue={supplier?.phone}
            />
          </Label>
          <ErrorMessage message={errors.phone?.message} />
        </div>
        <div className="flex flex-col">
          <Label title={t("common.Address")} required>
            <Input
              {...register("adresse")}
              name="adresse"
              type="text"
              defaultValue={supplier?.adresse}
            />
          </Label>
          <ErrorMessage message={errors.adresse?.message} />
        </div>
      </form>
    </Modal>
  );
}
